import { get, isEmpty, isNil } from "lodash";
import { useMemo } from "react";
import { useQuery } from "shared/hooks/useApi";
import { ATTACHMENTS } from "utils/api/graphql/queries/attachments";
import { COLLECTIVE_PLANS } from "utils/api/graphql/queries/collective-quotes";
import { OFFERS } from "utils/api/graphql/queries/offers";
import { INSURANCE_PLAN_ATTACHMENTS_TYPES } from "utils/constants";
import { collectiveCatalog, rangeDocumentsFromPlan } from "./helpers";

const PLANS_PAGE_SIZE = 50;

const useCollectivePlans = (processId) => {
  const { data, loading } = useQuery(COLLECTIVE_PLANS, {
    variables: {
      where: { processes: { id: processId }, active: true },
      take: PLANS_PAGE_SIZE,
    },
    skip: isNil(processId),
    fetchPolicy: "cache-and-network",
  });
  return { plans: get(data, "insurancePlans.data") || [], loading };
};

export const useCollectiveCatalog = (processId) => {
  const { plans, loading: plansLoading } = useCollectivePlans(processId);
  const { data, loading: offersLoading } = useQuery(OFFERS, {
    variables: {
      where: { processes: { id: processId } },
      take: PLANS_PAGE_SIZE,
    },
    skip: isNil(processId),
    fetchPolicy: "cache-and-network",
  });
  const offers = get(data, "offers.data");
  const catalog = useMemo(
    () => collectiveCatalog(plans, offers),
    [plans, offers],
  );
  return { ...catalog, plans, loading: plansLoading || offersLoading };
};

export const useCollectivePlanDocuments = (plan, translation) => {
  const planId = get(plan, "id");
  const planAttachments = get(plan, "attachments");
  const { data } = useQuery(ATTACHMENTS, {
    skip: isNil(planId) || !isEmpty(planAttachments),
    variables: {
      isIn: { type: Object.values(INSURANCE_PLAN_ATTACHMENTS_TYPES) },
      where: {
        AND: [{ insurancePlans: { id: planId } }, { project: null }],
      },
    },
    fetchPolicy: "cache-and-network",
  });

  return rangeDocumentsFromPlan(
    {
      ...plan,
      attachments: !isEmpty(planAttachments)
        ? planAttachments
        : get(data, "attachments.data", []),
      params: get(plan, "params"),
    },
    translation,
  );
};

export default useCollectivePlans;
